import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Play, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listSalesVideos, deleteSalesVideo, getAssetBlob } from "@/lib/api";
import { isMutationLocked, statusLabel, statusGroup, TONE_CLASS } from "@/lib/salesVideoStatus";
import { hasSalesVideoResult } from "@/lib/salesVideoUx";

const dateLabel = (value) => {
  const date = new Date(value);
  return value && Number.isFinite(date.getTime()) ? date.toLocaleString("id-ID") : "—";
};

export default function SalesVideoHistory({ userId }) {
  const [jobs, setJobs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [media, setMedia] = useState(null);
  const mounted = useRef(false);
  const request = useRef(0);
  const actionGuard = useRef(false);

  const load = useCallback(async (nextCursor = null) => {
    const token = ++request.current;
    setLoading(true);
    setError(false);
    try {
      const data = await listSalesVideos({ limit: 20, ...(nextCursor ? { cursor: nextCursor } : {}) });
      if (!mounted.current || token !== request.current) return;
      const owned = (data.sales_videos || []).filter((job) => job.user_id === userId);
      setJobs((previous) => nextCursor ? [...previous, ...owned.filter((job) => !previous.some((item) => item.id === job.id))] : owned);
      setCursor(data.next_cursor || null);
    } catch {
      if (mounted.current && token === request.current) setError(true);
    } finally {
      if (mounted.current && token === request.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; request.current += 1; };
  }, [load]);
  useEffect(() => () => { if (media?.url) URL.revokeObjectURL(media.url); }, [media]);

  const remove = async (job) => {
    if (actionGuard.current || isMutationLocked(job) || job.user_id !== userId) return;
    if (!window.confirm("Hapus project ini dari riwayat? Catatan transaksi dan kredit tetap disimpan.")) return;
    actionGuard.current = true;
    setBusyId(job.id);
    try {
      await deleteSalesVideo(job.id);
      if (!mounted.current) return;
      request.current += 1;
      setLoading(false);
      setJobs((items) => items.filter((item) => item.id !== job.id));
      setMedia((current) => current?.id === job.id ? null : current);
      toast.success("Project dihapus dari riwayat.");
    } catch { if (mounted.current) toast.error("Project belum dapat dihapus. Muat ulang status lalu coba kembali."); }
    finally { actionGuard.current = false; if (mounted.current) setBusyId(null); }
  };

  const openResult = async (job, download = false) => {
    if (actionGuard.current || job.user_id !== userId || !hasSalesVideoResult(job)) return;
    actionGuard.current = true;
    setBusyId(job.id);
    try {
      const blob = await getAssetBlob(job.id);
      if (!mounted.current) return;
      const url = URL.createObjectURL(blob);
      if (download) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `video-jualan-${job.id}.mp4`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else setMedia({ id: job.id, url, title: job.product_description });
    } catch { if (mounted.current) toast.error("Gagal memuat hasil video. Coba kembali."); }
    finally { actionGuard.current = false; if (mounted.current) setBusyId(null); }
  };

  return (
    <section className="space-y-4" data-testid="sales-video-history">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-semibold text-slate-900">Project Video Jualan</h2>
        <Button variant="outline" size="sm" disabled={loading || Boolean(busyId)} onClick={() => load()}>Muat ulang</Button>
      </div>
      {loading && !jobs.length && <p className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Memuat riwayat project…</p>}
      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">Gagal memuat riwayat project. <button className="underline" onClick={() => load()}>Coba kembali</button></div>}
      {!loading && !error && !jobs.length && <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500" data-testid="sales-history-empty">Belum ada project Video Jualan. <Link className="font-medium text-blue-700" to="/video-jualan">Buat video</Link></div>}
      <div className="grid gap-4 md:grid-cols-2" data-testid="sales-history-list">
        {jobs.map((job) => {
          const group = statusGroup(job);
          const result = hasSalesVideoResult(job);
          const draft = job.history_status === "DRAFT" || (["draft", "concept_ready"].includes(job.status) && !isMutationLocked(job));
          return (
            <article key={job.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`sales-history-${job.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="min-w-0 flex-1 break-words text-sm font-semibold text-slate-900">{job.product_description || "Video Jualan"}</h3>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${TONE_CLASS[group.tone]}`}>{job.history_status || group.label}</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">{statusLabel(job)}</p>
              <p className="mt-3 text-xs text-slate-500">{job.duration_seconds ? `${job.duration_seconds} detik · ` : ""}{job.resolution || ""}{job.aspect_ratio ? ` · ${job.aspect_ratio}` : ""}</p>
              <p className="mt-1 break-words text-xs text-slate-500">{job.model_display_name || job.model_snapshot?.display_name || job.model_key || "Model belum tersedia"}{job.provider ? ` · ${job.provider}` : ""}</p>
              {job.template_snapshot?.name && <p className="mt-1 text-xs text-slate-500">Template: {job.template_snapshot.name}</p>}
              <p className="mt-3 text-xs text-slate-400">Dibuat: {dateLabel(job.created_at)}</p>
              <p className="mt-1 text-xs text-slate-400">Diperbarui: {dateLabel(job.updated_at)}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                {draft && <Link to={`/video-jualan?project=${encodeURIComponent(job.id)}`} className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white" data-testid={`continue-sales-${job.id}`}>{isMutationLocked(job) ? "Lanjutkan" : "Lanjutkan / Edit"}</Link>}
                {!draft && !result && <Link to={`/video-jualan?project=${encodeURIComponent(job.id)}`} className="text-sm font-medium text-blue-700">Lihat status</Link>}
                {result && <>
                  <Button size="sm" disabled={Boolean(busyId)} onClick={() => openResult(job)}><Play className="mr-1 h-4 w-4" />Preview</Button>
                  <Button size="sm" variant="outline" disabled={Boolean(busyId)} onClick={() => openResult(job, true)}><Download className="mr-1 h-4 w-4" />Download</Button>
                </>}
                <Button variant="ghost" size="sm" className="ml-auto text-slate-400 hover:text-red-600" aria-label="Hapus project" disabled={Boolean(busyId) || isMutationLocked(job)} onClick={() => remove(job)}>
                  {busyId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </div>
            </article>
          );
        })}
      </div>
      {cursor && <Button variant="outline" disabled={loading || Boolean(busyId)} onClick={() => load(cursor)}>{loading ? "Memuat…" : "Muat lebih banyak"}</Button>}
      <Dialog open={Boolean(media)} onOpenChange={(open) => { if (!open) setMedia(null); }}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle className="break-words">{media?.title || "Video Jualan"}</DialogTitle></DialogHeader>
          {media?.url && <video controls playsInline src={media.url} className="max-h-[70vh] w-full rounded-xl" data-testid="sales-history-player" />}
        </DialogContent>
      </Dialog>
    </section>
  );
}
